import axios from "axios";
import {
  LEGAL_COMPLIANCE_API_ENDPOINTS,
  LEGAL_COMPLIANCE_PRESENTATION_VERSION,
  LEGAL_DOCUMENTS_PUBLIC_PATH,
} from "@/features/legal/config/legal.config";
import type {
  CurrentLegalCompliance,
  LegalCompliance,
  LegalComplianceAcceptPayload,
  LegalComplianceAcceptanceResponse,
  LegalComplianceCurrentResponse,
  LegalDocument,
  LegalDocumentsResponse,
} from "@/features/legal/types/legalCompliance.type";
import { fetcher } from "@/shared/lib/fetcher.client";
import { getApiBaseUrl } from "@/shared/utils/api.util";

// Captured at load like the fetcher's base URL; only parsed when called, so a
// missing configuration fails the request instead of the module import.
const API_BASE_URL = getApiBaseUrl();

export const getCurrentLegalCompliance = async (): Promise<CurrentLegalCompliance | null> => {
  const response = await fetcher.get<LegalComplianceCurrentResponse>(LEGAL_COMPLIANCE_API_ENDPOINTS.CURRENT);
  const { legal_compliance: legalCompliance, can_accept: canAccept } = response.data;
  return legalCompliance && { ...legalCompliance, can_accept: canAccept !== false };
};

// Exact HTML copy of the version displayed; a superseded version answers 409.
export const downloadCurrentLegalCompliance = async (version: string): Promise<Blob> => {
  const response = await fetcher.get<Blob>(LEGAL_COMPLIANCE_API_ENDPOINTS.DOWNLOAD_CURRENT, {
    params: { version },
    responseType: "blob",
  });
  return response.data;
};

// Confirms the version, the label and the screen actually displayed.
export const acceptLegalCompliance = async (
  legalCompliance: Pick<LegalCompliance, "version" | "action_label">,
): Promise<LegalComplianceAcceptanceResponse> => {
  const payload: LegalComplianceAcceptPayload = {
    version: legalCompliance.version,
    accepted: true,
    presentation_version: LEGAL_COMPLIANCE_PRESENTATION_VERSION,
    action_label: legalCompliance.action_label,
  };
  const response = await fetcher.post<LegalComplianceAcceptanceResponse>(LEGAL_COMPLIANCE_API_ENDPOINTS.ACCEPT, payload);
  return response.data;
};

// Public endpoint (no authentication) at the API root: bypasses the
// `fetcher`, whose baseURL is scoped to `/api/benef`. Read by the login page.
export const getPublicLegalDocuments = async (): Promise<LegalDocument[]> => {
  const url = new URL(LEGAL_DOCUMENTS_PUBLIC_PATH, API_BASE_URL).toString();
  const response = await axios.get<LegalDocumentsResponse>(url);
  return response.data.legal_publication?.documents ?? [];
};
